/**
 * Who this cat belongs to -- the claim token's ONE home in the client.
 *
 * ARCH-PLAN §4.3 (C4b): a localStorage token is not an identity for a
 * shipped game, but the fix is WHERE IT LIVES, not what it is.  The 48-hex
 * token the server mints (packages/server/src/store.js, mintToken) plus the
 * cats.json record it names is a good, hand-repairable identity system with
 * atomic persistence; accounts would be scope creep.  So this file only
 * decides which shelf the client keeps its half of that pair on, and every
 * read or write of the token -- boot's returning-player check, the join
 * path, the claim handler, the stale-token retry (net/index.js re-exports
 * the two functions below so those call sites and app/boot.js share one
 * import) -- goes through localToken()/saveToken() and never touches a
 * storage API itself.
 *
 * TWO SHELVES, one live at a time, never mixed:
 *
 *   WEB: localStorage under "catworld.token" -- byte-for-byte the
 *   behaviour the token block at the bottom of net/index.js always had:
 *   same "non-empty string or null" read, the same set/remove write.  This
 *   is a hard contract, not a convention: every cat on the public street is
 *   held by a token already sitting in some player's localStorage, and a
 *   read path that drifted even a byte would orphan the lot.  The key was
 *   "lilysworld.token" until the game was renamed, and the read still
 *   ADOPTS that value when the new key is empty (legacy-storage.js) --
 *   which is how the rename cost nobody their cat.
 *   packages/client/test/identity.test.mjs pins both halves mechanically,
 *   against the live implementation.
 *
 *   DESKTOP (activates in Phase 5): the preload bridge's size-capped file
 *   store, keeping <userData>/identity.json.  An Electron renderer's
 *   localStorage dies with a cache clear -- one "free up space" click away
 *   from orphaning a cat -- while userData survives clears and reinstalls,
 *   and rides Steam Cloud once userData sync is ticked in Steamworks
 *   (§4.3: one checkbox; do it).  When the bridge's file store is present
 *   it IS the shelf: a failed file write does NOT fall back to
 *   localStorage, because a token that sometimes lands in the
 *   cache-clearable store is exactly the bug this shelf exists to close.
 *
 * WHAT WS-17'S PRELOAD MUST EXPOSE -- two methods on
 * globalThis.catWorldDesktop, beside visitDenied (net/desktop.js holds
 * the bridge's documented shape); the BridgeFiles typedef below is the
 * same contract, machine-checked:
 *
 *   readTextFile(name)         -> string | null
 *   writeTextFile(name, text)  -> boolean
 *
 *   - `name` is a bare filename from the shell's allowlist
 *     ("identity.json" is the only name this module ever passes); the MAIN
 *     process refuses anything containing a path separator -- the renderer
 *     never chooses a path, only a shelf.
 *   - SYNCHRONOUS (ipcRenderer.sendSync is fine): one read at boot, one
 *     write per claim.  The claim path is synchronous today -- boot.js
 *     reads the token before deciding whether to show the join card -- and
 *     an async bridge would ripple await through it for nothing.
 *   - Size-capped at 64 KiB both ways: a write past the cap returns false,
 *     a file past the cap reads as null.  identity.json is under 200
 *     bytes; the cap is there so a hand-mangled megabyte cannot ride sync
 *     IPC into the render thread.
 *   - NEVER THROWS across the bridge.  Missing file, unreadable file,
 *     refused write: null / false.  (Every call here is try/catch-guarded
 *     anyway -- a half-loaded preload must read as "no bridge".)
 *   - The write lands ATOMICALLY (temp file + rename inside userData --
 *     the same move packages/server/src/store.js documents for cats.json),
 *     so a crash mid-write can never tear the file.
 *
 * HAND RECOVERY -- the server store's repair doctrine, extended to the
 * client end, which §4.3 asks for in so many words.  identity.json and
 * cats.json are both small, pretty-printed JSON, and both are meant to be
 * copied by hand:
 *
 *   - Move a desktop cat to a new machine: copy identity.json into the new
 *     install's userData directory (the local world's cats.json travels
 *     the same way).  Nothing else identifies the cat.
 *   - Rescue a cat whose token you know (it is right there in cats.json,
 *     beside the record): on the web,
 *     localStorage.setItem("catworld.token", "<the token>") in the
 *     console; on desktop, paste the token into identity.json.  The read
 *     below accepts the written shape {"v":1,...,"token":"..."}, a JSON
 *     string, or a BARE HEX LINE (16..64 chars, the server's own
 *     TOKEN_SHAPE, case-folded) -- so a repair is a paste, not a JSON
 *     quoting exercise.
 *   - A mangled identity.json cannot crash anything: unreadable is simply
 *     "no identity", and the next claim rewrites the file whole -- the
 *     posture store.js takes toward a corrupt cats.json.
 *
 * Storage is hostile everywhere and every failure here is SILENT: absent
 * (node), throwing on property access (sandboxed iframes), throwing on
 * write (private mode, quota), a bridge method that lied about its shape.
 * Losing the token costs this cat's persistence across sessions -- a real
 * loss, but strictly smaller than the game not starting, so a player whose
 * storage is gone still plays; they just meet a new cat next time.
 */
import { desktopBridge } from "./net/desktop.js";
import { getAdopting } from "./legacy-storage.js";

/** The web shelf's one localStorage key.  The key IS the compatibility
    contract with every token already in the wild, so it has been renamed
    exactly once -- with the rename itself carried by the read below, which
    adopts LEGACY_TOKEN_KEY's value rather than starting an empty shelf
    beside it (legacy-storage.js holds the argument). */
const TOKEN_KEY = "catworld.token";

/** What TOKEN_KEY was called while the game was "Lily's World".  Read, never
    written, and never deleted: a rolled-back build must still find its cat. */
const LEGACY_TOKEN_KEY = "lilysworld.token";

/** The desktop shelf's one filename -- the only name this module ever
    hands to the bridge's file store.  Exported for the tests and for
    WS-17's allowlist. */
export const IDENTITY_FILE = "identity.json";

/** A token a hand could have pasted: the server's TOKEN_SHAPE
    (packages/server/src/store.js) -- lowercase hex, 16..64 chars.  Only
    the bare-paste repair path is judged against this; tokens from the
    written JSON shape (machine-lowercase already) and from localStorage
    are passed through untouched, exactly as they always were. */
const PASTED_TOKEN = /^[0-9a-f]{16,64}$/;

/**
 * The file-store half of the WS-17 bridge contract (see header).
 * @typedef {{ readTextFile(name: string): string | null,
 *             writeTextFile(name: string, text: string): boolean }} BridgeFiles
 */

/** The bridge's file store, or null -- and null for a HALF-shaped bridge
    too: a preload that exposes one method but not the other half-loaded,
    and half-loaded reads as "no bridge" (net/desktop.js's posture). */
function bridgeFiles() {
  const b = desktopBridge();
  return b && typeof b.readTextFile === "function" &&
              typeof b.writeTextFile === "function"
    ? /** @type {BridgeFiles} */ (b)
    : null;
}

/** localStorage, or null where even touching it throws. */
function storage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

/**
 * identity.json's token, read forgivingly -- the header's three accepted
 * shapes.  The bare-hex test runs BEFORE JSON.parse on purpose: a pasted
 * all-digit token is valid JSON that parses to a NUMBER, and testing the
 * trimmed text first is what keeps "paste the hex, save" working
 * regardless of which sixteen characters the hex happens to be.
 * @param {BridgeFiles} files
 * @returns {string | null}
 */
function fileToken(files) {
  let text;
  try {
    text = files.readTextFile(IDENTITY_FILE);
  } catch {
    return null;
  }
  if (typeof text !== "string" || !text) return null;
  const bare = text.trim().toLowerCase();
  if (PASTED_TOKEN.test(bare)) return bare;
  try {
    const raw = JSON.parse(text);
    const t = raw && typeof raw === "object" ? raw.token : raw;
    return typeof t === "string" && t ? t : null;
  } catch {
    return null;
  }
}

/** The saved claim token, or null. */
export function localToken() {
  const files = bridgeFiles();
  if (files) return fileToken(files);
  const s = storage();
  if (!s) return null;
  try {
    const t = getAdopting(s, TOKEN_KEY, LEGACY_TOKEN_KEY);
    return typeof t === "string" && t ? t : null;
  } catch {
    return null;
  }
}

/**
 * Persist (or with null, forget) the claim token.  Best effort.
 *
 * On desktop, "forget" is a write of {"token": null}, not a delete: the
 * bridge contract stays two methods, and a token-less file reads as "no
 * identity" above -- same outcome, smaller surface.  savedAt is for the
 * hand-recovery story only (two copies of identity.json on two machines:
 * keep the newer), never read back here.
 * @param {string | null | undefined} token
 */
export function saveToken(token) {
  const files = bridgeFiles();
  if (files) {
    try {
      files.writeTextFile(
        IDENTITY_FILE,
        JSON.stringify({ v: 1, savedAt: Date.now(), token: token || null },
                       null, 2) + "\n",
      );
    } catch {
      // A bridge that throws lied about its contract; the posture is the
      // same as a full localStorage -- the cat plays on, unremembered.
    }
    return;
  }
  const s = storage();
  if (!s) return;
  try {
    if (token) s.setItem(TOKEN_KEY, token);
    else s.removeItem(TOKEN_KEY);
  } catch {
    // Private mode or quota; same loss, same silence.
  }
}
